/* eslint-disable no-confusing-arrow */
import { LocalizedComponentProps } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkCarousel } from 'uitk-react-carousel';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkListItem } from 'uitk-react-list';
import { UitkHeading } from 'uitk-react-text';
import { ActivityDestinationViewSearch, ActivityGroupType } from '../../../../common/__generated__/api';
import { getSeeAllURL } from '../../../../helpers/common-helper';
import { onClickScrollToTarget } from '../../../../helpers/smooth-scroll';
import { SearchCriteriaStore } from '../../../../stores';
import { useStores } from '../../../../utils/hooks';
import { ActivityTileCard } from './simple-result-card';

export interface ActivityCarouselProps extends LocalizedComponentProps {
  activityGroup: ActivityDestinationViewSearch.ActivityGroups;
  regionName?: string;
  regionId?: string;
  searchCriteriaStore?: SearchCriteriaStore;
  pageId?: string;
}

export const ActivityCarousel: React.FC<ActivityCarouselProps> = ({ activityGroup, pageId }) => {
  const { analytics, searchCriteriaStore, routeStore } = useStores();

  if (!pageId) {
    return null;
  }

  const { activityTiles, name, action, type } = activityGroup;
  let seeAllUrl = '';

  const handleSrpTileClick = (activity: ActivityDestinationViewSearch.ActivityInlineFragment): void | undefined => {
    searchCriteriaStore.scrollPosition = window.pageYOffset;

    if (activity.analytics) {
      analytics.trackEvent(activity.analytics.referrerId, activity.analytics.linkName, analytics.omnitureData);
    }
  };

  const renderActivityCard = (index: number, activity: any, groupType: string): JSX.Element =>
    groupType === ActivityGroupType.RECOMMENDATIONS ? (
      <UitkListItem className="max-height">
        <ActivityTileCard
          key={`acitivity-tile-card-${activity.id}`}
          tag="li"
          activity={activity}
          loading={false}
          index={index}
          onClick={(): void => handleSrpTileClick(activity)}
          activityGroup={groupType}
        />
      </UitkListItem>
    ) : (
      <ActivityTileCard
        key={`acitivity-tile-card-${activity.id}`}
        activity={activity}
        loading={false}
        index={index}
        onClick={(): void => handleSrpTileClick(activity)}
        activityGroup={groupType}
      />
    );

  const handleShowAllClick = (action: any): void | null => {
    onClickScrollToTarget({ scrollTo: 0 });
    analytics.trackEvent(action.analytics.referrerId, action.analytics.linkName, analytics.omnitureData);
  };

  const renderShowAllLink = (element: JSX.Element): JSX.Element => (
    <Link to={seeAllUrl} onClick={handleShowAllClick.bind(null, action)} type="button">
      {element}
    </Link>
  );

  const CarouselProps = {
    carouselName: 'activity-carousel',
    itemsVisible: {
      lg: 3,
      md: 3,
      sm: 1
    },
    pageBy: 1,
    peekWidth: {
      lg: 12,
      md: 12,
      sm: 72
    },
    buttonText: {
      nextButton: 'next',
      prevButton: 'previous'
    },
    navigationIconId: {
      next: 'activity-carousel-next-button-title',
      prev: 'activity-carousel-prev-button-title'
    },
    wrapItems: false
  };

  if (action) {
    seeAllUrl = getSeeAllURL(action.url, routeStore.queryParams);
  }

  return (
    <section
      className="all-t-margin-six all-x-margin-three activity-list-carousel"
      data-testid={`${name}-activity-carousel--section`}
    >
      <UitkLayoutFlex justifyContent="space-between" alignItems="center">
        <UitkLayoutFlexItem className="all-b-margin-two">
          <UitkHeading tag="h2" size={4}>
            {name}
          </UitkHeading>
        </UitkLayoutFlexItem>
        <Viewport>
          <ViewMedium>
            <>{action && <UitkLayoutFlexItem>{renderShowAllLink(<>{action.label}</>)}</UitkLayoutFlexItem>}</>
          </ViewMedium>
        </Viewport>
      </UitkLayoutFlex>

      {activityTiles &&
        (activityTiles.length > 1 ? (
          <UitkCarousel {...CarouselProps} className="uitk-carousel-should-snap all-b-margin-two">
            {activityTiles.map((acitivity, index) => renderActivityCard(index, acitivity, type))}
          </UitkCarousel>
        ) : (
          renderActivityCard(0, activityTiles[0], type)
        ))}

      <Viewport>
        <ViewSmall>
          <>
            {action && renderShowAllLink(<UitkSecondaryButton isFullWidth={true}>{action.label}</UitkSecondaryButton>)}
          </>
        </ViewSmall>
        <ViewMedium />
      </Viewport>
    </section>
  );
};
